
import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';

/**
 * Modal Reutilizable y Responsivo
 * @param {boolean} isOpen - Estado de visibilidad
 * @param {function} onClose - Función para cerrar
 * @param {string} title - Título del modal
 * @param {string} size - 'sm' | 'md' | 'lg' | 'xl' | 'full'
 */
const Modal = ({ isOpen, onClose, title, children, size = 'md', footer, preventCloseOnOverlayClick = false }) => {
    
    // Bloquear scroll del body cuando está abierto
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const sizeClasses = {
        sm: 'max-w-md',
        md: 'max-w-xl',
        lg: 'max-w-3xl',
        xl: 'max-w-5xl',
        full: 'max-w-full m-4'
    };

    return createPortal(
        <div className="fixed inset-0 z-[60] overflow-y-auto" role="dialog" aria-modal="true">
            {/* Flex container for true centering */}
            <div className="flex min-h-screen items-center justify-center p-4 text-center">
                
                {/* Overlay backdrop */}
                <div 
                    className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 backdrop-blur-sm" 
                    aria-hidden="true" 
                    onClick={() => {
                        if (!preventCloseOnOverlayClick) onClose();
                    }}
                ></div>

                {/* Modal Panel */}
                <div className={`
                    relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all 
                    w-full ${sizeClasses[size]}
                `}>
                    
                    {/* Header */}
                    <div className="bg-white px-4 py-3 border-b border-gray-100 flex justify-between items-center">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                            {title}
                        </h3>
                        <button 
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500 hover:bg-gray-100 p-1 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="px-4 py-3 max-h-[75vh] overflow-y-auto">
                        {children}
                    </div>

                    {/* Footer */}
                    {footer && (
                         <div className="bg-gray-50 px-4 py-2 sm:px-6 flex flex-col sm:flex-row-reverse gap-2">
                            {footer}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default Modal;
